import { useCallback, useMemo } from 'react';
import { useGetRoleTypesQuery } from '../queries/getRoleTypes.query.generated';
import { useRemoveAllowedRoleMutation } from '../queries/removeAllowedRole.mutation.generated';

/**
 * Roles that are allowed in `usertoroles`. Everything else should only live
 * in `default_role` and will be removed from the junction table on sync.
 */
const ALLOWED_IN_USERTOROLES = new Set(['driver']);

/**
 * Self-healing hook for the `usertoroles` junction table.
 *
 * The only role that belongs in `usertoroles` is `driver`. All other roles
 * (dealer, dealer-admin, dealer-super-admin) must only live in the user's
 * `default_role`. Call `syncRoles` after any role change to strip out entries
 * that shouldn't be there.
 */
export function useRoleSwap() {
  const { data: roleTypesData } = useGetRoleTypesQuery({
    fetchPolicy: 'cache-first',
  });
  const [removeRole] = useRemoveAllowedRoleMutation();

  /** Map of role name -> role id */
  const roleIdMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const rt of roleTypesData?.roletypes ?? []) {
      map.set(rt.name, rt.id);
    }
    return map;
  }, [roleTypesData]);

  /** Role IDs that should NOT be in usertoroles */
  const disallowedRoleIds = useMemo(() => {
    const ids: number[] = [];
    for (const [name, id] of roleIdMap) {
      if (!ALLOWED_IN_USERTOROLES.has(name)) {
        ids.push(id);
      }
    }
    return ids;
  }, [roleIdMap]);

  /**
   * Remove any role entries from `usertoroles` that don't belong there.
   * Safe to call multiple times -- removals are idempotent.
   */
  const syncRoles = useCallback(
    async (userId: number) => {
      await Promise.all(
        disallowedRoleIds.map(roleId =>
          removeRole({ variables: { userId, roleId } }),
        ),
      );
    },
    [disallowedRoleIds, removeRole],
  );

  return { syncRoles, ready: roleIdMap.size > 0 };
}
